import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MARK_PAID_CONFIRM_ACTION_WITH_RECEIPT,
  MARK_PAID_CONFIRM_CANCEL,
  MARK_PAID_CONFIRM_MESSAGE_P1,
  MARK_PAID_CONFIRM_TITLE,
  MARK_PAID_RECORD_AND_SEND_HINT,
} from "@shared/markPaidXeroWorkflow";
import {
  maskCustomerEmailForReceiptConfirm,
  type FinancePaymentReceiptMode,
} from "@shared/financePaymentReceiptMode";

export type MarkPaidReceiptModeSubmit = {
  receiptMode: FinancePaymentReceiptMode;
  paidAt: number;
  internalReason: string;
  paymentReference?: string;
  confirmCustomerReceipt?: boolean;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerEmail?: string | null;
  pending?: boolean;
  onSubmit: (input: MarkPaidReceiptModeSubmit) => void;
};

function todayLocalDateInput(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function MarkPaidReceiptModeDialog({
  open,
  onOpenChange,
  customerEmail,
  pending,
  onSubmit,
}: Props) {
  const [mode] = useState<FinancePaymentReceiptMode>("record_and_send");
  const [paidDate, setPaidDate] = useState(todayLocalDateInput);
  const [reason, setReason] = useState("");
  const [reference, setReference] = useState("");
  const [confirmSend, setConfirmSend] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPaidDate(todayLocalDateInput());
    setReason("");
    setReference("");
    setConfirmSend(false);
  }, [open]);

  const masked = maskCustomerEmailForReceiptConfirm(customerEmail);
  const canSubmit =
    Boolean(paidDate) &&
    reason.trim().length > 0 &&
    confirmSend &&
    !pending;

  const submit = () => {
    if (!canSubmit) return;
    const paidAt = new Date(`${paidDate}T12:00:00`).getTime();
    onSubmit({
      receiptMode: mode,
      paidAt,
      internalReason: reason.trim(),
      paymentReference: reference.trim() || undefined,
      confirmCustomerReceipt: mode === "record_and_send" ? true : undefined,
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="max-w-lg">
        <AlertDialogHeader>
          <AlertDialogTitle>{MARK_PAID_CONFIRM_TITLE}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 text-sm text-muted-foreground">
              <p>{MARK_PAID_CONFIRM_MESSAGE_P1}</p>
              <p>
                This records payment as received but does{" "}
                <strong className="font-semibold text-foreground">not</strong> send it to Xero.
              </p>

              <div className="space-y-2">
                <Label className="text-foreground">Payment received date</Label>
                <Input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  disabled={pending}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Internal reason</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g. Bank transfer already received; recording for delivery gate"
                  disabled={pending}
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label className="text-foreground">Payment reference / evidence note (optional)</Label>
                <Input
                  value={reference}
                  onChange={(e) => setReference(e.target.value)}
                  placeholder="Bank ref, cheque no., etc."
                  disabled={pending}
                />
              </div>

              <div className="space-y-2 rounded-md border p-3">
                <p className="text-foreground font-medium">Customer receipt</p>
                <label className="flex items-start gap-2 cursor-pointer">
                  <span>
                    <span className="text-foreground font-medium">Customer confirmation is required</span>
                    <span className="block text-xs">{MARK_PAID_RECORD_AND_SEND_HINT} Cleo will also send the WhatsApp confirmation now, or request “YES” first when the 24-hour window is closed.</span>
                  </span>
                </label>
                {(
                  <div className="mt-2 space-y-2 rounded bg-amber-50 p-2 text-amber-950 dark:bg-amber-950/30 dark:text-amber-50">
                    <p>
                      The customer will receive an email at <strong>{masked}</strong>.
                    </p>
                    <label className="flex items-start gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={confirmSend}
                        onChange={(e) => setConfirmSend(e.target.checked)}
                        disabled={pending}
                        className="mt-1"
                      />
                      <span>I confirm the customer should receive this receipt by email and Cleo WhatsApp.</span>
                    </label>
                  </div>
                )}
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col gap-2 sm:flex-row sm:justify-end">
          <AlertDialogCancel disabled={pending}>{MARK_PAID_CONFIRM_CANCEL}</AlertDialogCancel>
          <Button
            type="button"
            variant="default"
            disabled={!canSubmit}
            onClick={(e) => {
              e.preventDefault();
              submit();
            }}
          >
            {pending
              ? "Recording…"
              : MARK_PAID_CONFIRM_ACTION_WITH_RECEIPT}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
